import { api } from '@/shared/lib/api';
import type {
  TenantProfile,
  UpdateProfileRequest,
  StoreSettings,
  UpdateStoreSettingsRequest,
  DeliverySettings,
  UpdateDeliverySettingsRequest,
  PaymentMethodsResponse,
  UpdatePaymentMethodsRequest,
  FileUploadResponse,
  MenuDomainResponse,
  UpdateMenuDomainRequest,
  CheckSlugResponse,
  WhatsAppPreviewResponse,
  StoreKnowledge,
  UpdateStoreKnowledgeRequest,
  StoreFaqEntry,
  StoreFaqEntryRequest,
  StoreFaqList,
  ReorderStoreFaqRequest,
  StoreClosure,
  StoreClosuresResponse,
  CreateStoreClosureRequest,
} from '../types/settings.types';

const TENANTS = '/tenants';

// --- Tenant Profile ---

export async function getProfile(): Promise<TenantProfile> {
  const res = await api.get<TenantProfile>(`${TENANTS}/profile`);
  return res.data;
}

export async function updateProfile(data: UpdateProfileRequest): Promise<TenantProfile> {
  const res = await api.put<TenantProfile>(`${TENANTS}/profile`, data);
  return res.data;
}

// --- Store Settings ---

export async function getStoreSettings(): Promise<StoreSettings> {
  const res = await api.get<StoreSettings>(`${TENANTS}/settings/store`);
  return res.data;
}

export async function updateStoreSettings(data: UpdateStoreSettingsRequest): Promise<StoreSettings> {
  const res = await api.put<StoreSettings>(`${TENANTS}/settings/store`, data);
  return res.data;
}

// --- Conhecimento da loja: Sobre, observação de pagamento, FAQ e fechamentos ---
//
// Rotas com `settings.read` e `settings.write` (change store-and-catalog-knowledge,
// decisão 13). Uma API anterior responde 404, e o cartão some.

export async function getStoreKnowledge(): Promise<StoreKnowledge> {
  const res = await api.get<StoreKnowledge>(`${TENANTS}/settings/store-knowledge`);
  return res.data;
}

export async function updateStoreKnowledge(data: UpdateStoreKnowledgeRequest): Promise<StoreKnowledge> {
  const res = await api.put<StoreKnowledge>(`${TENANTS}/settings/store-knowledge`, data);
  return res.data;
}

export async function getStoreFaq(): Promise<StoreFaqList> {
  const res = await api.get<StoreFaqList>(`${TENANTS}/settings/store-faq`);
  return res.data;
}

export async function createStoreFaqEntry(data: StoreFaqEntryRequest): Promise<StoreFaqEntry> {
  const res = await api.post<StoreFaqEntry>(`${TENANTS}/settings/store-faq`, data);
  return res.data;
}

export async function updateStoreFaqEntry(
  entryId: string,
  data: StoreFaqEntryRequest,
): Promise<StoreFaqEntry> {
  const res = await api.put<StoreFaqEntry>(`${TENANTS}/settings/store-faq/${entryId}`, data);
  return res.data;
}

export async function deleteStoreFaqEntry(entryId: string): Promise<void> {
  await api.delete(`${TENANTS}/settings/store-faq/${entryId}`);
}

/** A ordem nova de todas as perguntas; devolve a lista regravada. */
export async function reorderStoreFaq(data: ReorderStoreFaqRequest): Promise<StoreFaqList> {
  const res = await api.put<StoreFaqList>(`${TENANTS}/settings/store-faq/order`, data);
  return res.data;
}

export async function getStoreClosures(): Promise<StoreClosuresResponse> {
  const res = await api.get<StoreClosuresResponse>(`${TENANTS}/settings/store-closures`);
  return res.data;
}

export async function createStoreClosure(data: CreateStoreClosureRequest): Promise<StoreClosure> {
  const res = await api.post<StoreClosure>(`${TENANTS}/settings/store-closures`, data);
  return res.data;
}

/** 204, idempotente: cancelar de novo também é 204. */
export async function cancelStoreClosure(closureId: string): Promise<void> {
  await api.delete(`${TENANTS}/settings/store-closures/${closureId}`);
}

// --- WhatsApp Order Preview ---

/** Sample message rendered by the same builder used for real orders. */
export async function getWhatsAppPreview(): Promise<WhatsAppPreviewResponse> {
  const res = await api.get<WhatsAppPreviewResponse>('/orders/whatsapp-preview');
  return res.data;
}

// --- Delivery Settings ---

export async function getDeliverySettings(): Promise<DeliverySettings> {
  const res = await api.get<DeliverySettings>(`${TENANTS}/settings/delivery`);
  return res.data;
}

export async function updateDeliverySettings(
  data: UpdateDeliverySettingsRequest,
): Promise<DeliverySettings> {
  const res = await api.put<DeliverySettings>(`${TENANTS}/settings/delivery`, data);
  return res.data;
}

// --- Payment Methods ---

export async function getPaymentMethods(): Promise<PaymentMethodsResponse> {
  const res = await api.get<PaymentMethodsResponse>(`${TENANTS}/settings/payment-methods`);
  return res.data;
}

export async function updatePaymentMethods(
  data: UpdatePaymentMethodsRequest,
): Promise<PaymentMethodsResponse> {
  const res = await api.put<PaymentMethodsResponse>(`${TENANTS}/settings/payment-methods`, data);
  return res.data;
}

// --- Menu Domain ---

export async function getMenuDomain(): Promise<MenuDomainResponse> {
  const res = await api.get<MenuDomainResponse>(`${TENANTS}/settings/menu-domain`);
  return res.data;
}

export async function updateMenuDomain(data: UpdateMenuDomainRequest): Promise<MenuDomainResponse> {
  const res = await api.put<MenuDomainResponse>(`${TENANTS}/settings/menu-domain`, data);
  return res.data;
}

export async function checkSlug(
  slug: string,
  excludeTenantId?: string,
): Promise<CheckSlugResponse> {
  const params: Record<string, string> = { slug };
  if (excludeTenantId) params.excludeTenantId = excludeTenantId;
  const res = await api.get<CheckSlugResponse>(`${TENANTS}/check-slug`, { params });
  return res.data;
}

// --- File Upload ---

export async function uploadFile(file: File, context: 'logo' | 'product'): Promise<FileUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('context', context);

  const res = await api.post<FileUploadResponse>('/files/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}
